import { ControllerHelper, PALMSController, PlayListNodeDownloadDto, PlaylistDownloadDto, PlaylistStateDownloadDto, PlaylistUploadDto, PostController, PostDownloadDto, PostInteraction, SessionTemplateController } from 'collaboration-service';
import CommentsRatingsView from 'components/General/CommentsRatingsView';
import SafeHTML from 'components/SafeHTML/SafeHTML';
import TabsBar from 'components/TabsBar/TabsBar';
import { Button, Icon, Loader, Theme, useAboveBreakpoint, useBelowBreakpointOrEqual, useThemePart } from 'imaginarity-react-ui';
import _ from 'lodash';
import * as React from 'react';
import { Actions, WorkingActions } from 'services/ApplicationState/Actions';
import { ApplicationState, useAppDispatch, useAppSelector } from 'services/ApplicationState/ApplicationState';
import { bannerTop } from 'services/Config';
import { convertMinutesToDaysHoursMinutes, getCriticalAndPendingCounts, initPostDownloadDto, shallowCompare, sortReferenceIdsInNodes, visit } from 'services/Helpers';
import { getMediaLink } from 'services/Helpers/MediaHelpers';
import { gotoNamedRouteMethod, linkToRoute } from 'services/Helpers/RoutingHelper';
import ScrollHelper from 'services/Helpers/ScrollHelper';
import { getTranslated, getTranslatedStandardDate } from 'services/Helpers/TranslationHelpers';
import History from 'services/History';
import { useImgI18N } from 'services/ImgI18N';
import { isAdmin } from 'services/StoreDependantHelpers';
import PlaylistDetailBanner from './Edit/PlaylistDetail/PlaylistDetailBanner';
import PlaylistDetailError from './Edit/PlaylistDetail/PlaylistDetailError';
import PlaylistDetailNextNodes from './Edit/PlaylistDetail/PlaylistDetailNextNodes';
import PlaylistDetailNodesToShow from './Edit/PlaylistDetail/PlaylistDetailNodesToShow';
import { PlaylistDetailSC as T } from './PlaylistDetailSC';
import Timeline from './Timeline/Timeline';

const mapper = (state: ApplicationState) => ({
    contentLanguage: state.contentLanguage,
    routeParams: state.params,
    // sidebarRightOpen: state.playlistsState.rightSidebar,
    playlistsStates: state.playlistsState.playlistStatesTopic,
});

export declare type LocalStateEnum = 'Finished' | 'On Track' | 'Overdue' | 'Warning' | 'Optional';
export interface NodeState {
    state: LocalStateEnum;
    finished: number;
}

export interface RefPostsInterface { [id: string]: PostDownloadDto }
interface PlaylistDetailProps {
}

const PlaylistDetail = (p: PlaylistDetailProps) => {
    const { routeParams, contentLanguage, /*sidebarRightOpen,*/ playlistsStates } = useAppSelector(mapper, shallowCompare);

    const dispatch = useAppDispatch();

    const [loading, setLoading] = React.useState<boolean>(false);
    const [allNodes, setAllNodes] = React.useState<PlayListNodeDownloadDto[]>([]);
    const [palmsLinks, setPalmsLinks] = React.useState<Record<string, string>>({});
    // const [nodeContentVisible, setNodeContentVisible] = React.useState<boolean[]>([]);
    const [nodeContentVisible, setNodeContentVisible] = React.useState<boolean[]>(() => {
        const initialVisibility = allNodes.map((n, index) => {
            const firstChild = n.children?.[0];
            const isFallThru = firstChild?.transitionFunction === "FallThru";
            return index === 0 && isFallThru;
            // return true;
        });

        // console.log("Initial Visibility State:", initialVisibility);
        return initialVisibility;
    });

    const [playlistState, setPlaylistState] = React.useState<PlaylistStateDownloadDto>();
    const [refPosts, setRefPosts] = React.useState<RefPostsInterface>({});
    const [scrollHelper] = React.useState(new ScrollHelper());
    const { t } = useImgI18N("playlist");
    const id = routeParams?.id as string;
    const dataOrig = playlistState?.playlist;
    const data = dataOrig as PlayListNodeDownloadDto;

    const prevNodes = sortReferenceIdsInNodes(playlistState?.prevNodes ?? []);
    const curNodes = sortReferenceIdsInNodes(playlistState?.curNodes ?? []);
    const nextNodes = sortReferenceIdsInNodes(playlistState?.nextNodes ?? []);
    const hasNextNodes = nextNodes.length > 0;
    const nextNodeHeadline = hasNextNodes ? <T.UpcomingNodesHeadline>{t(nextNodes.length === 1 ? "upcoming phase" : "upcoming phases")}</T.UpcomingNodesHeadline> : undefined;
    const nodesToShow = prevNodes.concat(curNodes);
    const dueToDate = playlistState?.plannedEndDate;
    const curNode = playlistState?.curNodes[0];
    const dueDate = curNode?.calculatedDueDate;
    const banner = getMediaLink(dataOrig, d => d?.media);
    const isMobile = useBelowBreakpointOrEqual("largeMobile");
    const isTablet = useAboveBreakpoint("largeMobile");
    const contentWidth = useThemePart((t: Theme) => t.content.width);
    const [error, setError] = React.useState<boolean>(false);
    const currentState = React.useMemo(() => {
        const toRet = _.find(playlistsStates, p => p.playlist.id === id);
        if (toRet === undefined && (playlistsStates ?? []).length > 0) {
            setError(true);
        }
        return toRet;
    }, [playlistsStates, id]);
    // console.log("PlaylistDetail.tsx::101 => nodeContentVisible", nodeContentVisible);


    React.useEffect(() => {
        if (allNodes && allNodes.length > 0 && curNode) {
            setNodeContentVisible(allNodes.map((n, index) => {
                const firstChild = n.children?.[0];
                const isFallThru = firstChild?.transitionFunction === "FallThru";
                return isFallThru || n.id === curNode.id;
                // return n.id === curNode.id;
            }));
        }
        if (playlistState?.state === "Finished") {
            setNodeContentVisible(_.map(playlistState?.prevNodes, () => true));
        }
    }, [allNodes, playlistState, curNode]);



    React.useEffect(() => {
        let x: NodeJS.Timeout | undefined = undefined;
        const redo = () => {
            //console.log("updating!");
            dispatch(WorkingActions.WIKI.loadPlaylistState());
            x = setTimeout(redo, 1000 * 30);
        }
        redo();
        return () => {
            if (x) clearTimeout(x);
        }
    }, [dispatch]);

    const loadPlaylistStates = React.useMemo(() => async () => {
        if (id) {
            setPlaylistState(x => {
                setLoading(x === undefined);
                return x;
            });
            if (currentState !== undefined) {
                const s = _.clone(currentState);
                const nodes: PlayListNodeDownloadDto[] = [];
                visit((s.playlist as PlaylistDownloadDto).startNode, (cur) => _.compact(_.map(cur.children, edge => edge.to)), cur => nodes.push(cur));
                setAllNodes(curNodes => _.isEqual(curNodes, nodes) ? curNodes : nodes);
                let ids: string[] = [];
                _.forEach(s.curNodes, n => {
                    ids = ids.concat(n.referenceIds ?? []);
                });
                _.forEach(s.prevNodes, n => {
                    ids = ids.concat(n.referenceIds ?? []);
                });
                _.forEach(s.nextNodes, n => {
                    ids = ids.concat(n.referenceIds ?? []);
                });
                ids = _.uniq(ids);
                const contents = (s.playlist as PlaylistDownloadDto).contents;
                const pContents = _.filter(contents, c => c.referenceType === "PALMS");
                const pIds = _.map(pContents, p => p.referenceId!);
                const sContents = _.filter(contents, c => c.referenceType === "Session");
                const sIds = _.map(sContents, c => c.referenceId!);
                const pTasks = _.map(_.difference(ids, pIds, sIds), refid => ControllerHelper.singleCall({ id: refid, context: s.id }, PostController.GetSinglePostWithContext, true));
                const tasks = _.concat(pTasks, _.map(sIds, refid => ControllerHelper.singleCall({ id: refid }, SessionTemplateController.GetSessionTemplate) as unknown as Promise<PostDownloadDto>));
                const res = await Promise.all(tasks);
                setPlaylistState(curS => _.isEqual(curS, s) ? curS : s);
                setRefPosts(p => {
                    const toRet = _.clone(p);
                    _.forEach(_.compact(res), r => toRet[r.id] = r);
                    _.forEach(pContents, p => toRet[p.referenceId!] = initPostDownloadDto("", "PALMS", p.referenceId));
                    if (!_.isEqual(p, toRet))
                        return toRet;
                    return p;
                });
                const l = async () => {
                    const palmsContents = _.filter(contents, c => c.referenceType === "PALMS");
                    if (palmsContents.length > 0) {
                        const res = await ControllerHelper.singleCall({ moduleIds: _.map(palmsContents, p => p.referenceId!), playlistId: s.playlist.id }, PALMSController.GetLinks, true);
                        setPalmsLinks(res);
                    }
                    else
                        setPalmsLinks({});
                };
                l();

            }

            setLoading(false);
        }
    }, [id, currentState]);

    const reloadPlaylistState = React.useCallback(async (toBeDone?: string) => {
        dispatch(Actions.setPlaylistItemToDone(toBeDone));
        for (let i = 1; i < 15; i++) {
            setTimeout(() => dispatch(WorkingActions.WIKI.loadPlaylistState()), 1000 * i);
        }
    }, [dispatch]);

    React.useEffect(() => {
        if (!_.isEqual(currentState, playlistState))
            loadPlaylistStates();
    }, [loadPlaylistStates, currentState, playlistState]);

    React.useEffect(() => {
        if (Object.keys(refPosts).length > 0) {
            scrollHelper.scrollTo(curNode?.id ?? '');
        }
    }, [refPosts, curNode, scrollHelper]);

    const getCurrentLearningStatus = React.useMemo(() => (): LocalStateEnum => {
        if (playlistState?.optional)
            return "Optional";
        if (!curNode)
            return playlistState?.state === "Finished" ? "Finished" : "On Track";
        switch (curNode?.state) {
            case "Finished":
                return "Finished";
            case "Overdue":
                return "Overdue";
            default:
                return "On Track";
        }
    }, [playlistState, curNode]);

    const isAllAtOnce = playlistState && playlistState.playlist ? (playlistState.playlist as PlaylistUploadDto).sequencingMode === "AllAtOnce" : false;
    const AAOcontents = playlistState && (playlistState.playlist as PlaylistUploadDto).contents;

    React.useEffect(() => {
        const loadSinglePostsWithContextByContextId = async (contextId: string) => {
            const tasks = _.map(AAOcontents, content => ControllerHelper.singleCall({ id: content.referenceId ?? '', context: contextId }, PostController.GetSinglePostWithContext, true));
            const res = await Promise.all(tasks);
            setRefPosts(p => {
                const toRet = _.clone(p);
                _.forEach(_.compact(res), r => toRet[r.id] = r);
                return toRet;
            });
        };
        if (AAOcontents) {
            const s = _.find(playlistsStates, p => p.playlist.id === id);
            if (s)
                loadSinglePostsWithContextByContextId(s.id);
        }
    }, [AAOcontents, id, playlistsStates]);

    const playlistIsFinished = playlistState?.state === "Finished";
    const loadingData = !isAllAtOnce && Object.keys(refPosts).length === 0;


    const res = playlistState ? getCriticalAndPendingCounts(playlistState) : { criticalCount: 0, itemsToBeDone: 0 };

    const [remainingDurationInMin, setRemainingDurationInMin] = React.useState<number>(0);


    React.useEffect(() => {
        if (playlistState) {
            const sumDuration = (nodes: PlayListNodeDownloadDto[] | undefined): number => {
                if (!nodes) {
                    return 0;
                }
                return nodes.reduce((total, node) => total + (node.durationInMinutes || 0), 0);
            };
            const prevNodesDuration = sumDuration(playlistState.prevNodes);
            const curNodesDuration = sumDuration(playlistState.curNodes);
            const nextNodesDuration = sumDuration(playlistState.nextNodes);
            const totalDurationAllNodes = prevNodesDuration + curNodesDuration + nextNodesDuration;
            const remaining = totalDurationAllNodes - prevNodesDuration;
            setRemainingDurationInMin(remaining);
        }
    }, [playlistState]);

    const initialIdRef = React.useRef<string | undefined>(undefined);

    React.useEffect(() => {
        if (error === true) {
            if (initialIdRef.current === undefined) {
                initialIdRef.current = id;
            } else if (id && id !== initialIdRef.current) {
                window.location.reload();
            }
        }

    }, [id, error]);

    const remainingDuration = convertMinutesToDaysHoursMinutes(remainingDurationInMin, t, true);

    const showRatings = playlistState?.playlist && (playlistState.playlist.forbidRatings !== true);
    const showComments = playlistState?.playlist?.allowComments !== false;

    const playlistStateIsOptional = playlistState?.optional;

    type PlaylistTab = "description" | "timeline" | "comment" | "rating" | "";

    const [selectedTab, setSelectedTab] = React.useState<PlaylistTab>("description");

    const tabsSelect = React.useMemo(() => (idx: number | undefined) => {
        const tabMapping: { [key: number]: { tab: PlaylistTab; scrollTo: string } } = {
            0: { tab: "rating", scrollTo: "bottom" },
            1: { tab: "comment", scrollTo: "bottom" },
            2: { tab: "description", scrollTo: "info" },
            3: { tab: "timeline", scrollTo: "info" },
        };

        const selected = tabMapping[idx ?? -1];
        if (selected) {
            setSelectedTab(selected.tab);
            scrollHelper.scrollTo(selected.scrollTo);
        } else {
            setSelectedTab("");
        }
    }, [scrollHelper]);

    const admin = isAdmin("WIKI");
    const stickyHeader = true;

    if (error)
        return <PlaylistDetailError
            banner={banner}
            stickyHeader={stickyHeader}
            id={id}
            bannerTop={bannerTop}
        />;
    else return (
        <div style={{ padding: isMobile ? "10px 0" : 0, paddingBottom: 50 }} ref={scrollHelper.getRef("info")}>

            {loading ?
                <Loader active size="small" light />
                :
                <div style={{ position: "relative", marginTop: -2 }}>
                    <PlaylistDetailBanner
                        banner={banner}
                        stickyHeader={stickyHeader}
                        id={id}
                        bannerTop={bannerTop}
                        data={data}
                        playlistState={playlistState}
                        tabsSelect={tabsSelect}
                    />

                    <Loader active={loadingData} size="small" light infoText={playlistState ? t('loading playlist states') : t("loading playlist phases")} />
                    {data &&
                        <>
                            <T.OuterContainerTopGridSticky stickyHeader={stickyHeader} >
                                <T.TopGrid style={{ gridTemplateColumns: isMobile ? "1fr max-content max-content" : "1fr 1fr 1fr 1fr 1fr max-content", columnGap: isMobile ? 20 : 0, padding: "10px 0 0 0" }}>
                                    <div>
                                        <T.TopGridHeadline>{playlistStateIsOptional ? t("your playlist status") : contentWidth < 580 ? t("learning status") : t("current learning status")}</T.TopGridHeadline>
                                        {/* <T.TopGridContent color={(isAllAtOnce || playlistStateIsOptional) ? "On Track" : playlistIsFinished ? "Finished" : getCurrentLearningStatus()} style={{ fontWeight: 600 }}>
                                            {t((isAllAtOnce || playlistStateIsOptional) ? "on track" : playlistIsFinished ? "done" : getCurrentLearningStatus())} */}
                                        <T.TopGridContent
                                            color={playlistStateIsOptional ? undefined : playlistIsFinished ? "Finished" : isAllAtOnce ? "open" : getCurrentLearningStatus()}
                                            style={{ fontWeight: 600 }}>
                                            {t(playlistStateIsOptional ? "optional" : playlistIsFinished ? "done" : isAllAtOnce ? "open" : getCurrentLearningStatus())}
                                        </T.TopGridContent>
                                    </div>
                                    {(!isAllAtOnce && contentWidth >= 580) ?
                                        <div>
                                            <T.TopGridHeadline>{t("completed")}</T.TopGridHeadline>
                                            <T.TopGridContent style={{ display: "grid", gridTemplateColumns: "max-content 1fr" }}>
                                                <div>
                                                    {res.criticalCount} / {res.itemsToBeDone}
                                                </div>

                                                <div />
                                            </T.TopGridContent>
                                        </div>
                                        : <div />
                                    }
                                    {(isTablet && remainingDurationInMin > 0 && contentWidth >= 520) ?
                                        <div>
                                            <T.TopGridHeadline>{t("remaining duration")}</T.TopGridHeadline>
                                            <T.TopGridContent>
                                                {remainingDuration}
                                            </T.TopGridContent>
                                        </div>
                                        : <div />
                                    }
                                    {(!isAllAtOnce && dueDate && !playlistStateIsOptional) ?
                                        <div style={{ paddingRight: isMobile ? 60 : 0 }}>
                                            <T.TopGridHeadline>{t("next date")}</T.TopGridHeadline>
                                            <T.TopGridContent color={(new Date(dueDate as Date)) >= (new Date()) ? undefined : "Overdue"}>
                                                {dueDate && !isNaN(new Date(dueDate as Date).getTime()) ? (
                                                    (new Date(dueDate as Date)) >= (new Date())
                                                        ? getTranslatedStandardDate(new Date(dueDate as Date), "PP")
                                                        : getTranslatedStandardDate(new Date(dueDate as Date), "PP")
                                                    // : formatDistance(new Date(dueDate as Date), new Date(), { locale: getDateFnsLng, addSuffix: true })
                                                ) : (
                                                    t("invalid date")
                                                )}
                                            </T.TopGridContent>

                                        </div>
                                        : <div />
                                    }

                                    {(!isAllAtOnce && dueToDate && isTablet && !playlistStateIsOptional) ?
                                        <div>
                                            <T.TopGridHeadline>{t("final date")}</T.TopGridHeadline>
                                            <T.TopGridContent>{getTranslatedStandardDate(dueToDate, "PP")}</T.TopGridContent>
                                        </div>
                                        : <div />
                                    }
                                    <div>
                                        {!isTablet &&
                                            <div style={{ marginTop: -40 }}>
                                                {admin &&
                                                    <Button
                                                        kind='primary'
                                                        tooltip={{ tooltipText: t("edit this playlist in new window"), position: "bottom" }}
                                                        asHtmlLinkToRoute={linkToRoute(`playlist/editPlaylist/${id}`)}
                                                        asHtmlLinkToRouteTarget="_blank"
                                                        floated='right'
                                                        icon="playlist edit"
                                                    />
                                                }
                                                <Button
                                                    kind='cancelButton'
                                                    tooltip={{ tooltipText: t("playlist overview"), position: "bottom" }}
                                                    floated='right'
                                                    icon="home"
                                                    onClick={gotoNamedRouteMethod("playlist")}
                                                />
                                                <Button
                                                    kind='cancelButton'
                                                    tooltip={{ tooltipText: t("back"), position: "bottom" }}
                                                    floated='right'
                                                    icon="chevron left"
                                                    onClick={() => History.goBack()}
                                                />
                                            </div>
                                        }
                                        {playlistState && playlistState.playlist.forbidRatings !== true &&
                                            <Button iconColor={selectedTab === 'rating' ? "@accentRed" : undefined} kind={selectedTab === 'rating' ? "cancelButton" : 'cancelButton'} tooltip={{ tooltipText: t("goto ratings") }} floated='right' icon="star half" onClick={() => tabsSelect(0)} />
                                        }

                                        {playlistState && playlistState.playlist.allowComments &&
                                            <Button iconColor={selectedTab === 'comment' ? "@accentRed" : undefined} kind={selectedTab === 'comment' ? "cancelButton" : 'cancelButton'} tooltip={{ tooltipText: t("goto comments") }} floated='right' icon="comment" onClick={() => tabsSelect(1)} />
                                        }
                                        <Button iconColor={selectedTab === 'description' ? "@accentRed" : undefined} kind={selectedTab === 'description' ? "cancelButton" : 'cancelButton'} tooltip={{ tooltipText: t("{{what}} desctiption", { what: selectedTab === 'description' ? "hide" : "show" }) }} floated='right' icon="info" onClick={() => selectedTab === 'description' ? tabsSelect(undefined) : tabsSelect(2)} />
                                        {!isAllAtOnce && !playlistStateIsOptional &&
                                            <Button iconColor={selectedTab === 'timeline' ? "@accentRed" : undefined} kind={selectedTab === 'timeline' ? "cancelButton" : 'cancelButton'} tooltip={{ tooltipText: t("{{what}} dates", { what: selectedTab === 'timeline' ? "hide" : "show" }) }} floated='right' icon="calendar" onClick={() => selectedTab === 'timeline' ? tabsSelect(undefined) : tabsSelect(3)} />
                                        }
                                    </div>
                                </T.TopGrid>
                                {selectedTab !== 'timeline' &&
                                    <T.OpenArea onClick={() => tabsSelect(3)}>
                                        <Icon name='chevron down double' color='transparent' size="24px" />
                                    </T.OpenArea>
                                }
                            </T.OuterContainerTopGridSticky>

                            <T.OuterContainer style={{ borderBottom: "none", marginTop: 10 }}>
                                {selectedTab === "description" &&
                                    <>
                                        <T.Description>
                                            <Icon name="info" style={{ float: "left", marginRight: 10 }} marginTop={2} />
                                            {t("description")}
                                        </T.Description>
                                        <T.DescriptionText>
                                            {data && <SafeHTML
                                                html={(getTranslated(data.descriptions, contentLanguage)?.text ?? "").replace(/<p><br><\/p>/g, "")}
                                            />}
                                        </T.DescriptionText>
                                    </>
                                }
                                {selectedTab === "timeline" &&
                                    <>
                                        {!isAllAtOnce && !playlistStateIsOptional &&
                                            <T.TimelineSticky stickyHeader={stickyHeader}>
                                                {playlistState &&
                                                    <>
                                                        <T.Description>
                                                            <Icon name="calendar" style={{ float: "left", marginRight: 10 }} marginTop={2} />
                                                            {t("dates")}
                                                        </T.Description>
                                                        <Timeline data={playlistState} onClose={() => tabsSelect(undefined)} />
                                                    </>
                                                }
                                            </T.TimelineSticky>
                                        }

                                    </>
                                }


                                {/* {!isMobile && <div style={{ height: 40 }} />} */}
                                <PlaylistDetailNodesToShow
                                    nodesToShow={nodesToShow}
                                    playlistState={playlistState}
                                    nodeContentVisible={nodeContentVisible}
                                    setNodeContentVisible={setNodeContentVisible}
                                    scrollHelper={scrollHelper}
                                    refPosts={refPosts}
                                    reloadPlaylistState={reloadPlaylistState}
                                    palmsLinks={palmsLinks}
                                />
                                <div style={{ height: 8 }} />
                                {nextNodeHeadline}
                                <PlaylistDetailNextNodes
                                    playlistState={playlistState}
                                    refPosts={refPosts}
                                    reloadPlaylistState={reloadPlaylistState}
                                    palmsLinks={palmsLinks}
                                />

                                <div ref={scrollHelper.getRef("bottom")} style={{ height: 40, width: "100%" }} />


                                {playlistState && (showComments || showRatings) &&
                                    <>
                                        <TabsBar
                                            input={[
                                                {
                                                    title: t("ratings"),
                                                    icon: "star half",
                                                    active: selectedTab !== "comment",
                                                    hidden: !showRatings,
                                                },
                                                {
                                                    title: t("comments"),
                                                    icon: "comment",
                                                    active: selectedTab === "comment",
                                                    hidden: !showComments,
                                                },
                                            ]}
                                            onChange={tabsSelect}
                                            activeBold
                                        />
                                        <T.InfoBoxBottom mobile={isMobile}>
                                            {selectedTab === "comment" ?
                                                <CommentsRatingsView
                                                    post={playlistState?.playlist}
                                                    noLeftBorder
                                                    getIncoming={PostInteraction.GetPostComments}
                                                    addCommentRating={PostInteraction.AddPostComment}
                                                    onDeleteComment={PostInteraction.DeletePostComment}
                                                    onSubmitComment={PostInteraction.UpdatePostComment}
                                                    showNoPostInfo
                                                    autoFocus={false}
                                                />
                                                :
                                                <CommentsRatingsView
                                                    post={playlistState?.playlist}
                                                    noLeftBorder
                                                    asRating
                                                    getIncoming={PostInteraction.GetPostRatings}
                                                    addCommentRating={PostInteraction.AddPostRating}
                                                    filledColor={"@accentBlack"}
                                                    filledOut={true}
                                                    showNoPostInfo
                                                    autoFocus={false}
                                                />
                                            }
                                        </T.InfoBoxBottom>
                                    </>
                                }

                            </T.OuterContainer>
                        </>
                    }
                </div>
            }
        </div >
    );
}
export default PlaylistDetail;